import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { CREATE_DISEASE } from '../graphql/operations';
import { Disease, DiseaseInput } from '../types';

interface QuickDiseaseDialogProps {
  open: boolean;
  onClose: () => void;
  onDiseaseCreated: (disease: Disease) => void;
  initialName?: string;
}

export const QuickDiseaseDialog: React.FC<QuickDiseaseDialogProps> = ({
  open,
  onClose,
  onDiseaseCreated,
  initialName = '',
}) => {
  const [formData, setFormData] = useState<DiseaseInput>({
    name: initialName,
    icd10_code: '',
    short_description: '',
    is_active: true,
  });
  const [error, setError] = useState('');

  const [createDisease, { loading }] = useMutation(CREATE_DISEASE, {
    onCompleted: (data) => {
      if (data.createDisease) {
        onDiseaseCreated(data.createDisease);
        handleClose();
      }
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const handleClose = () => {
    setFormData({
      name: '',
      icd10_code: '',
      short_description: '',
      is_active: true,
    });
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Disease name is required');
      return;
    }

    setError('');
    await createDisease({
      variables: {
        input: formData,
      },
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add New Disease</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <TextField
            autoFocus
            label="Disease Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Atopic Dermatitis"
          />
          
          <TextField
            label="ICD-10 Code"
            fullWidth
            value={formData.icd10_code || ''}
            onChange={(e) => setFormData({ ...formData, icd10_code: e.target.value })}
            placeholder="e.g., L20.9"
          />
          
          <TextField
            label="Short Description"
            fullWidth
            multiline
            rows={2}
            value={formData.short_description || ''}
            onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
            placeholder="Brief description of the disease"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.name.trim()}
        >
          {loading ? 'Creating...' : 'Create Disease'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};